import { PendingRequestStore } from '../../data/pending-request.store';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent, SnackbarComponent, ToastType } from '@program-suite/ui';
import { MatRadioModule } from '@angular/material/radio';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { UI_FORM_IMPORTS } from '@program-suite/ui';
import { PendingRequestStatusEnum } from '@program-suite/data-common/program';
import { MatSnackBar } from '@angular/material/snack-bar';
import { distinctUntilChanged } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PendingRequestService } from '../../data/pending-request.service';
import { CustomDatePickerComponent } from "../../../../../../shared/ui-common/src/lib/components/form/custom-date-picker/custom-date-picker.component";

@Component({
  selector: 'lib-action-form',
  templateUrl: './action-form.component.html',
  styleUrl: './action-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    MatRadioModule,
    CommonModule,
    ReactiveFormsModule,
    ...UI_FORM_IMPORTS,
    CustomDatePickerComponent
],
})
export class ActionFormComponent implements OnInit {
  pendingRequestStore = inject(PendingRequestStore);
  pendingRequestService = inject(PendingRequestService);
  proposedEffectiveDate = input<string | null | undefined>(null);
  effectiveDate = input<string>();
  form: FormGroup;
  dateNow = new Date();
  minEffectiveDate: Date;
  maxEffectiveDate: Date;
  displayComments = signal(false);
  displayExclusion = signal(false);
  displayExclusionDate = signal(true);
  displayApprove =signal(true); 
  displayDeclined =signal(true);
  displayMoreInfo =signal(true);
  loading = signal(false);
  submissionGuid = input.required<string | null>();
  requestType =input.required<string | null>();
  title = '';

  closeModal = output<boolean>();

  constructor(
    private fb: FormBuilder,
    private snackbar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    // Set effective date constraints: today to 60 days in the future
    const today = new Date();
    this.minEffectiveDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.maxEffectiveDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 60);

    const effectiveDateValidator = (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      // Parse the date value - handle both string (YYYY-MM-DD) and Date objects
      let dateOnly: Date;
      if (typeof control.value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(control.value)) {
        // Parse YYYY-MM-DD string directly to avoid timezone issues
        const [year, month, day] = control.value.split('-').map(Number);
        dateOnly = new Date(year, month - 1, day);
      } else {
        const date = new Date(control.value);
        if (isNaN(date.getTime())) return null;
        dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }

      if (dateOnly < this.minEffectiveDate) {
        return { dateInPast: true };
      }
      if (dateOnly > this.maxEffectiveDate) {
        return { dateTooFar: true };
      }
      return null;
    };

    this.form = this.fb.group({
      status: this.fb.control<PendingRequestStatusEnum | null>(null, Validators.required),
      updatePolicyEffectiveDate: this.fb.control<boolean | null>(false, Validators.required),
      policyEffectiveDate: this.fb.control(null, effectiveDateValidator),
      removePriorWork: this.fb.control<boolean | null>(false, Validators.required),
      priorWorksEffectiveDate: this.fb.control(null),
      comments: this.fb.control(null),
    });
  }

  ngOnInit(): void {
    switch(this.requestType()){
      case 'Bind':
        this.title= 'Issue Policy';
        break;
      case 'Quote':
        this.title= 'Quote Status';
        break;
      case 'Renewal':
      case 'Rewrite':
        this.title= 'Quote Status';
        break;
      default:
        this.title = 'Quote Status';
        this.displayApprove.set(false);
        this.displayDeclined.set(false);
        break;
    }
    this.form
      .get('status')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        const commentsControl = this.form.get('comments');
        const priorWorksEffectiveDateControl = this.form.get('priorWorksEffectiveDate');
        const updatePolicyEffectiveDateControl = this.form.get('updatePolicyEffectiveDate');

        switch (value) {
          case PendingRequestStatusEnum.MoreInfo:
          case PendingRequestStatusEnum.Decline:
            this.displayComments.set(true);
            commentsControl?.setValidators([Validators.required]);
            commentsControl?.updateValueAndValidity({ emitEvent: false });
            this.displayExclusion.set(false);
            priorWorksEffectiveDateControl?.clearValidators();
            priorWorksEffectiveDateControl?.updateValueAndValidity({ emitEvent: false });      
            updatePolicyEffectiveDateControl?.clearValidators();
            updatePolicyEffectiveDateControl?.updateValueAndValidity({ emitEvent: false });
            break;

          case PendingRequestStatusEnum.Approve:
            this.displayComments.set(false);
            commentsControl?.clearValidators();
            commentsControl?.setValue('', { emitEvent: false });
            commentsControl?.markAsUntouched();
            switch (this.requestType()){
              case 'Bind':
                this.displayExclusion.set(true);
                commentsControl?.updateValueAndValidity({ emitEvent: false });
                priorWorksEffectiveDateControl?.setValidators([Validators.required]);
                priorWorksEffectiveDateControl?.updateValueAndValidity({ emitEvent: false });
                updatePolicyEffectiveDateControl?.setValidators([Validators.required]);
                updatePolicyEffectiveDateControl?.updateValueAndValidity({ emitEvent: false });
              break;             
            default:      
              break;
            }
            break;
        }
      });

    this.form
      .get('removePriorWork')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((value) => {
        const priorWorksEffectiveDateControl = this.form.get('priorWorksEffectiveDate');
        const updatePolicyEffectiveDateControl = this.form.get('updatePolicyEffectiveDate');
        if (value) {
          this.displayExclusionDate.set(false);
          priorWorksEffectiveDateControl?.clearValidators();
        } else {
          this.displayExclusionDate.set(true);
          priorWorksEffectiveDateControl?.setValidators([Validators.required]);
        }
        priorWorksEffectiveDateControl?.updateValueAndValidity({ emitEvent: false });
      });
  }

  submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const req = {
      ...this.form.value,
      id: this.submissionGuid(),
    };

    this.loading.set(true);

    this.pendingRequestStore
      .updatePendingRequestStatus(req)
      .then(async () => {
        let message = 'Bind request has been updated.';
        let redirectToPolicy = false;
        switch (req.status) {
          case PendingRequestStatusEnum.Approve:
            switch(this.requestType()){
              case 'Bind':  
              message = 'Bind request has been approved.';
              redirectToPolicy = true;
                break;
              case 'Quote':
                message = 'Quote has been approved.';
                break;
              case 'Renewal':
                message = 'Renewal has been approved.';
                break;
              case 'Rewrite':
                 message = 'Rewrite has been approved.';
                 break;
              default:
                break;
            }
          
            break;
          case PendingRequestStatusEnum.Decline:
            switch(this.requestType()){
              case 'Bind':  
              message = 'Bind request has been declined.';
              redirectToPolicy = true;
                break;
              case 'Quote':
                message = 'Quote has been declined.';
                break;
              case 'Renewal':
                message = 'Renewal has been declined.';
                break;
               case 'Rewrite':
                message = 'Rewrite has been declined.';
                break;
              default:
                message = 'Quote has been declined.';
                break;
            }
           
            break;
          case PendingRequestStatusEnum.MoreInfo:
            switch(this.requestType()){
              case 'Bind':  
              message = 'Bind request has been sent for more information.';
              redirectToPolicy = true;
                break;
              case 'Quote':
                message = 'Quote has been sent for more information.';
                break;
              case 'Renewal':
                message = 'Renewal has been sent for more information.';
                break;
                case 'Rewrite':
                message = 'Rewrite has been sent for more information.';
                break;
              default:
                break;
            }
         
            break;
        }

        this.closeModal.emit(true);

        if (redirectToPolicy && window.location.href.includes('submissions')) {
          const policyGuid = await this.pendingRequestService.getPolicyGuid(this.submissionGuid()!);

          if (policyGuid) {
            this.router.navigate(['/policies/summary', policyGuid]);
          }
        }

        this.snackbar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: { toastType: ToastType.Success, message },
        });
      })
      .catch((error) => {
        this.snackbar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          data: {
            toastType: ToastType.Error,
            message: 'An error occurred. Please try again.',
          },
        });
      })
      .finally(() => {
        this.pendingRequestStore.resource.reload();
        this.pendingRequestStore.setSubmissionReview(null);
        this.loading.set(false);
      });
  }
}
